import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { getToken } from "./token";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;
const API_SECRET = import.meta.env.VITE_API_SECRET;
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT ?? 30000);

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: Number.isFinite(API_TIMEOUT) ? API_TIMEOUT : 30000,
  withCredentials: true,
});

export interface ApiErrorInfo {
  status: number;
  message: string;
  url?: string;
  statusText?: string;
  code?: string;
}

type ApiErrorListener = (info: ApiErrorInfo | null) => void;
const apiErrorListeners = new Set<ApiErrorListener>();

export const ERROR_STATUS_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  REQUEST_TIMEOUT: 408,
} as const;

export function subscribeApiError(listener: ApiErrorListener) {
  apiErrorListeners.add(listener);
  return () => {
    apiErrorListeners.delete(listener);
  };
}

export function clearApiError() {
  apiErrorListeners.forEach((l) => l(null));
}

function emitApiError(info: ApiErrorInfo) {
  apiErrorListeners.forEach((l) => l(info));
}

apiClient.interceptors.request.use(
  async (config) => {
    config.headers = config.headers ?? {};
    const token = getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (API_KEY && API_SECRET) {
      const timestamp = Date.now().toString();
      const signature = await generateSignature(timestamp, API_SECRET);

      config.headers["x-api-key"] = API_KEY;
      config.headers["x-timestamp"] = timestamp;
      config.headers["x-signature"] = signature;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const statusText = error.response?.statusText || error.message;
    const url = error.config?.url;
    const isTimeout =
      error.code === "ECONNABORTED" || /timeout/i.test(error.message);
    const isNetwork = !error.response && !!error.request && !isTimeout;

    console.error("[Axios Error]", status, statusText, url, error.code);

    if (isTimeout) {
      emitApiError({
        status: ERROR_STATUS_CODES.REQUEST_TIMEOUT,
        message: "Request timeout. Please try again.",
        url,
        statusText,
        code: error.code,
      });
      return Promise.reject(error);
    }

    if (isNetwork) {
      emitApiError({
        status: 0,
        message: "Unable to connect to server.",
        url,
        statusText,
        code: error.code,
      });
      return Promise.reject(error);
    }

    if (status) {
      const errorInfo: ApiErrorInfo = {
        status,
        message: statusText,
        url,
        statusText,
        code: error.code,
      };

      if (status >= 400) {
        emitApiError(errorInfo);
      }

      switch (status) {
        case ERROR_STATUS_CODES.NOT_FOUND:
          console.warn("[404] Resource not found:", url);
          break;
        case ERROR_STATUS_CODES.UNAUTHORIZED:
          console.warn("[401] token invalid or expired");
          break;
        case ERROR_STATUS_CODES.FORBIDDEN:
          console.warn("[403] Forbidden - Access denied");
          break;
        case ERROR_STATUS_CODES.BAD_REQUEST:
          console.warn("[400] Bad request - Check request parameters");
          break;
        case ERROR_STATUS_CODES.SERVER_ERROR:
        case ERROR_STATUS_CODES.SERVICE_UNAVAILABLE:
          console.error("[5xx] Server error:", statusText);
          break;
      }
    }

    return Promise.reject(error);
  },
);

const generateSignature = async (
  timestamp: string,
  secret: string,
): Promise<string> => {
  const encoder = new TextEncoder();

  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    {
      name: "HMAC",
      hash: "SHA-256",
    },
    false,
    ["sign"],
  );

  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    encoder.encode(timestamp),
  );

  return Array.from(new Uint8Array(signature))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export type { AxiosRequestConfig };
